import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Autocomplete,
  SelectChangeEvent
} from '@mui/material';
import { 
  Add as AddIcon, 
  Delete as DeleteIcon, 
  Edit as EditIcon, 
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { api } from '../services/api';
import { authService } from '../services/auth';
import { getPortalAccess, resolveProviderId, canLoadProviderScopedData } from '../utils/portalAccess';
import { 
  Provider, 
  Client, 
  ClientISP, 
  ClientISPOutcome, 
  ClientISPExperience, 
  ClientISPSupport,
  ClientISPService,
  ClientISPCreate,
  Service
} from '../types';
import { fieldOnPageBlueSx, selectMenuWhitePaperProps } from '../mui/fieldOnPageBlue';
import AppDateField from '../components/AppDateField';
import { easternTodayYmd } from '../utils/easternTime';

interface ISPProps {
  selectedProvider?: number | null;
}

const ISP: React.FC<ISPProps> = ({ selectedProvider: adminSelectedProvider }) => {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [isps, setIsps] = useState<ClientISP[]>([]);
  const [ispOutcomes, setIspOutcomes] = useState<Record<number, ClientISPOutcome[]>>({});
  const [ispExperiences, setIspExperiences] = useState<Record<number, ClientISPExperience[]>>({});
  const [ispSupports, setIspSupports] = useState<Record<number, ClientISPSupport[]>>({});
  const [ispServices, setIspServices] = useState<Record<number, ClientISPService[]>>({});
  const [allServices, setAllServices] = useState<Service[]>([]);
  const [ispServicesSaving, setIspServicesSaving] = useState<Record<number, boolean>>({});
  const [ispServiceSearchInput, setIspServiceSearchInput] = useState<Record<number, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcomeTitleError, setOutcomeTitleError] = useState(false);
  const [experienceTitleError, setExperienceTitleError] = useState(false);
  const [supportTitleError, setSupportTitleError] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Selection states
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [expandedISP, setExpandedISP] = useState<number | false>(false);
  
  // Dialog states
  const [ispDialogOpen, setIspDialogOpen] = useState(false);
  const [outcomeDialogOpen, setOutcomeDialogOpen] = useState(false);
  const [experienceDialogOpen, setExperienceDialogOpen] = useState(false);
  const [supportDialogOpen, setSupportDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmCount, setDeleteConfirmCount] = useState(0);
  const [ispToDelete, setIspToDelete] = useState<number | null>(null);
  
  // Form states
  const [ispFormData, setIspFormData] = useState<ClientISPCreate>({
    provider_id: 0,
    client_id: 0,
    description: '',
    active_status: true,
    span_start_date: '',
    span_end_date: ''
  });
  const [outcomeFormData, setOutcomeFormData] = useState({
    provider_id: 0,
    client_id: 0,
    isp_id: 0,
    outcome_title: '',
    outcome_description: '',
    active_status: true
  });
  const [experienceFormData, setExperienceFormData] = useState({
    provider_id: 0,
    client_id: 0,
    isp_id: 0,
    outcome_id: undefined as number | undefined,
    experience_title: '',
    experience_description: '',
    active_status: true
  });
  const [supportFormData, setSupportFormData] = useState({
    provider_id: 0,
    client_id: 0,
    isp_id: 0,
    isp_service_id: undefined as number | undefined,
    support_title: '',
    support_description: '',
    active_status: true
  });
  
  // Editing states
  const [editingISP, setEditingISP] = useState<ClientISP | null>(null);
  const [editingOutcome, setEditingOutcome] = useState<ClientISPOutcome | null>(null);
  const [editingExperience, setEditingExperience] = useState<ClientISPExperience | null>(null);
  const [editingSupport, setEditingSupport] = useState<ClientISPSupport | null>(null);

  useEffect(() => {
    fetchProviders();
    setAdminStatus();
  }, [adminSelectedProvider]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const servicesData = await api.services.list(0, 1000);
        setAllServices(Array.isArray(servicesData) ? servicesData : []);
      } catch (err) {
        console.error('Error fetching services:', err);
      }
    };
    fetchServices();
  }, []);

  const setAdminStatus = async () => {
    try {
      const currentUser = await authService.getCurrentUser();
      setIsAdmin(currentUser.attributes['custom:admin_flag'] === 'true');
    } catch (err) {
      console.error('Error getting admin status:', err);
      setIsAdmin(false);
    }
  };

  const fetchProviders = async () => {
    try {
      setIsLoading(true);
      const access = await getPortalAccess();
      const currentUser = await authService.getCurrentUser();
      const providerId = resolveProviderId(
        access,
        adminSelectedProvider,
        currentUser.attributes['custom:provider_id'],
      );

      const response = await api.providers.list(0, 100);
      setProviders(response);

      if (providerId) {
        const provider = response.find((p: Provider) => Number(p.provider_id) === providerId)
          ?? await api.providers.get(providerId);
        setSelectedProvider(provider);
        await fetchClientsByProvider(provider.provider_id);
      } else if (access.requiresProviderSelection) {
        setSelectedProvider(null);
        setClients([]);
        setIsps([]);
      } else if (access.isAdmin) {
        setSelectedProvider(null);
      }
    } catch (err) {
      console.error('Error fetching providers:', err);
      setError('Failed to load providers. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchClientsByProvider = async (providerId: number | string) => {
    try {
      setIsLoading(true);
      const response = await api.clients.listByProvider(providerId, 100);
      setClients(response);
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError('Failed to load clients. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchISPsByClient = async (clientId: number | string) => {
    try {
      setIsLoading(true);
      // Omit active_status so both active and inactive ISPs are returned
      const response = await api.clientISP.getByClient(clientId);
      setIsps(Array.isArray(response) ? response : []);
      
      // Fetch outcomes, experiences, and supports for each ISP
      const ispArray = Array.isArray(response) ? response : [];
      const outcomesMap: Record<number, ClientISPOutcome[]> = {};
      const experiencesMap: Record<number, ClientISPExperience[]> = {};
      const supportsMap: Record<number, ClientISPSupport[]> = {};
      
      const servicesMap: Record<number, ClientISPService[]> = {};
      for (const isp of ispArray) {
        try {
          const [outcomes, experiences, supports, ispServicesData] = await Promise.all([
            api.clientISP.outcomes.list(isp.isp_id),
            api.clientISP.experiences.list(isp.isp_id),
            api.clientISP.supports.list(isp.isp_id),
            api.clientIspServices.getByIsp(isp.isp_id)
          ]);
          
          outcomesMap[isp.isp_id] = Array.isArray(outcomes) ? outcomes : [];
          experiencesMap[isp.isp_id] = Array.isArray(experiences) ? experiences : [];
          supportsMap[isp.isp_id] = Array.isArray(supports) ? supports : [];
          servicesMap[isp.isp_id] = Array.isArray(ispServicesData) ? ispServicesData : [];
        } catch (err) {
          console.error(`Error fetching ISP ${isp.isp_id} data:`, err);
          outcomesMap[isp.isp_id] = [];
          experiencesMap[isp.isp_id] = [];
          supportsMap[isp.isp_id] = [];
          servicesMap[isp.isp_id] = [];
        }
      }
      
      setIspOutcomes(outcomesMap);
      setIspExperiences(experiencesMap);
      setIspSupports(supportsMap);
      setIspServices(servicesMap);
    } catch (err) {
      console.error('Error fetching ISPs:', err);
      setError('Failed to load ISP records. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleProviderChange = (provider: Provider | null) => {
    setSelectedProvider(provider);
    setSelectedClient(null);
    setClients([]);
    setIsps([]);
    setIspOutcomes({});
    setIspExperiences({});
    setIspSupports({});
    setIspServices({});
    
    if (provider) {
      fetchClientsByProvider(provider.provider_id);
    }
  };

  const handleClientChange = (client: Client | null) => {
    setSelectedClient(client);
    setIsps([]);
    setIspOutcomes({});
    setIspExperiences({});
    setIspSupports({});
    setIspServices({});
    
    if (client) {
      fetchISPsByClient(client.client_id);
    }
  };

  const handleCreateISP = () => {
    if (!selectedProvider || !selectedClient) {
      setError('Please select a provider and client first.');
      return;
    }
    
    setIspFormData({
      provider_id: selectedProvider.provider_id as number,
      client_id: selectedClient.client_id as number,
      description: '',
      active_status: true,
      span_start_date: '',
      span_end_date: ''
    });
    setEditingISP(null);
    setIspDialogOpen(true);
  };

  const handleEditISP = (isp: ClientISP) => {
    setIspFormData({
      provider_id: isp.provider_id,
      client_id: isp.client_id,
      description: isp.description || '',
      active_status: isp.active_status,
      span_start_date: isp.span_start_date || '',
      span_end_date: isp.span_end_date || '',
      document_id: isp.document_id || null
    });
    setEditingISP(isp);
    setIspDialogOpen(true);
  };

  const handleSaveISP = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      if (editingISP) {
        await api.clientISP.update(editingISP.isp_id, {
          description: ispFormData.description,
          active_status: ispFormData.active_status,
          span_start_date: ispFormData.span_start_date || null,
          span_end_date: ispFormData.span_end_date || null,
          document_id: ispFormData.document_id || null
        });
        setSuccess('ISP record updated successfully.');
      } else {
        await api.clientISP.create(ispFormData);
        setSuccess('ISP record created successfully.');
      }
      
      setIspDialogOpen(false);
      if (selectedClient) {
        await fetchISPsByClient(selectedClient.client_id);
      }
    } catch (err: any) {
      console.error('Error saving ISP:', err);
      setError(err.response?.data?.detail || 'Failed to save ISP record.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteISP = (isp: ClientISP) => {
    setIspToDelete(isp.isp_id);
    setDeleteConfirmCount(0);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!ispToDelete) return;
    
    if (deleteConfirmCount < 2) {
      setDeleteConfirmCount(deleteConfirmCount + 1);
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      await api.clientISP.delete(ispToDelete);
      setSuccess('ISP record deleted successfully.');
      setDeleteDialogOpen(false);
      setIspToDelete(null);
      setDeleteConfirmCount(0);
      
      if (selectedClient) {
        await fetchISPsByClient(selectedClient.client_id);
      }
    } catch (err: any) {
      console.error('Error deleting ISP:', err);
      setError(err.response?.data?.detail || 'Failed to delete ISP record.');
      setDeleteDialogOpen(false);
      setIspToDelete(null);
      setDeleteConfirmCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateOutcome = (isp: ClientISP) => {
    setOutcomeFormData({
      provider_id: isp.provider_id,
      client_id: isp.client_id,
      isp_id: isp.isp_id,
      outcome_title: '',
      outcome_description: '',
      active_status: true
    });
    setOutcomeTitleError(false);
    setEditingOutcome(null);
    setOutcomeDialogOpen(true);
  };

  const handleEditOutcome = (outcome: ClientISPOutcome) => {
    setOutcomeFormData({
      provider_id: outcome.provider_id,
      client_id: outcome.client_id,
      isp_id: outcome.isp_id,
      outcome_title: outcome.outcome_title || '',
      outcome_description: outcome.outcome_description || '',
      active_status: outcome.active_status !== false
    });
    setOutcomeTitleError(false);
    setEditingOutcome(outcome);
    setOutcomeDialogOpen(true);
  };

  const handleSaveOutcome = async () => {
    if (!outcomeFormData.outcome_title.trim()) {
      setOutcomeTitleError(true);
      setError('Outcome title is required.');
      return;
    }
    setOutcomeTitleError(false);
    try {
      setIsLoading(true);
      setError(null);
      
      if (editingOutcome) {
        const contentChanged =
          outcomeFormData.outcome_title !== (editingOutcome.outcome_title || '') ||
          outcomeFormData.outcome_description !== (editingOutcome.outcome_description || '');
        const statusChanged = outcomeFormData.active_status !== (editingOutcome.active_status !== false);

        if (contentChanged) {
          await api.clientISP.outcomes.update(editingOutcome.outcome_id, {
            outcome_title: outcomeFormData.outcome_title,
            outcome_description: outcomeFormData.outcome_description,
            active_status: outcomeFormData.active_status
          });
          setSuccess('Outcome revised successfully. Prior version kept for historical visits.');
        } else if (statusChanged) {
          await api.clientISP.outcomes.update(editingOutcome.outcome_id, {
            active_status: outcomeFormData.active_status
          });
          setSuccess(outcomeFormData.active_status ? 'Outcome activated.' : 'Outcome deactivated.');
        } else {
          setOutcomeDialogOpen(false);
          return;
        }
      } else {
        await api.clientISP.outcomes.create(outcomeFormData.isp_id, {
          provider_id: outcomeFormData.provider_id,
          client_id: outcomeFormData.client_id,
          isp_id: outcomeFormData.isp_id,
          outcome_title: outcomeFormData.outcome_title,
          outcome_description: outcomeFormData.outcome_description
        });
        setSuccess('Outcome created successfully.');
      }
      
      setOutcomeDialogOpen(false);
      if (selectedClient) {
        await fetchISPsByClient(selectedClient.client_id);
      }
    } catch (err: any) {
      console.error('Error saving outcome:', err);
      setError(err.response?.data?.detail || 'Failed to save outcome.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleOutcomeActive = async (outcome: ClientISPOutcome, active: boolean) => {
    try {
      setIsLoading(true);
      setError(null);
      await api.clientISP.outcomes.update(outcome.outcome_id, { active_status: active });
      setSuccess(active ? 'Outcome activated.' : 'Outcome deactivated. Linked active experiences were also deactivated.');
      if (selectedClient) {
        await fetchISPsByClient(selectedClient.client_id);
      }
    } catch (err: any) {
      console.error('Error updating outcome status:', err);
      setError(err.response?.data?.detail || 'Failed to update outcome status.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateExperience = (isp: ClientISP, outcomeId?: number) => {
    setExperienceFormData({
      provider_id: isp.provider_id,
      client_id: isp.client_id,
      isp_id: isp.isp_id,
      outcome_id: outcomeId,
      experience_title: '',
      experience_description: '',
      active_status: true
    });
    setExperienceTitleError(false);
    setEditingExperience(null);
    setExperienceDialogOpen(true);
  };

  const handleEditExperience = (experience: ClientISPExperience) => {
    setExperienceFormData({
      provider_id: experience.provider_id,
      client_id: experience.client_id,
      isp_id: experience.isp_id,
      outcome_id: experience.outcome_id || undefined,
      experience_title: experience.experience_title || '',
      experience_description: experience.experience_description || '',
      active_status: experience.active_status !== false
    });
    setExperienceTitleError(false);
    setEditingExperience(experience);
    setExperienceDialogOpen(true);
  };

  const handleSaveExperience = async () => {
    if (!experienceFormData.experience_title.trim()) {
      setExperienceTitleError(true);
      setError('Experience title is required.');
      return;
    }
    setExperienceTitleError(false);
    try {
      setIsLoading(true);
      setError(null);
      
      if (editingExperience) {
        const contentChanged =
          (experienceFormData.outcome_id || null) !== (editingExperience.outcome_id || null) ||
          experienceFormData.experience_title !== (editingExperience.experience_title || '') ||
          experienceFormData.experience_description !== (editingExperience.experience_description || '');
        const statusChanged = experienceFormData.active_status !== (editingExperience.active_status !== false);

        if (contentChanged) {
          await api.clientISP.experiences.update(editingExperience.experience_id, {
            outcome_id: experienceFormData.outcome_id || null,
            experience_title: experienceFormData.experience_title,
            experience_description: experienceFormData.experience_description,
            active_status: experienceFormData.active_status
          });
          setSuccess('Experience revised successfully. Prior version kept for historical visits.');
        } else if (statusChanged) {
          await api.clientISP.experiences.update(editingExperience.experience_id, {
            active_status: experienceFormData.active_status
          });
          setSuccess(experienceFormData.active_status ? 'Experience activated.' : 'Experience deactivated.');
        } else {
          setExperienceDialogOpen(false);
          return;
        }
      } else {
        await api.clientISP.experiences.create(experienceFormData.isp_id, {
          provider_id: experienceFormData.provider_id,
          client_id: experienceFormData.client_id,
          isp_id: experienceFormData.isp_id,
          outcome_id: experienceFormData.outcome_id,
          experience_title: experienceFormData.experience_title,
          experience_description: experienceFormData.experience_description
        });
        setSuccess('Experience created successfully.');
      }
      
      setExperienceDialogOpen(false);
      if (selectedClient) {
        await fetchISPsByClient(selectedClient.client_id);
      }
    } catch (err: any) {
      console.error('Error saving experience:', err);
      setError(err.response?.data?.detail || 'Failed to save experience.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleExperienceActive = async (experience: ClientISPExperience, active: boolean) => {
    try {
      setIsLoading(true);
      setError(null);
      await api.clientISP.experiences.update(experience.experience_id, { active_status: active });
      setSuccess(active ? 'Experience activated.' : 'Experience deactivated.');
      if (selectedClient) {
        await fetchISPsByClient(selectedClient.client_id);
      }
    } catch (err: any) {
      console.error('Error updating experience status:', err);
      setError(err.response?.data?.detail || 'Failed to update experience status.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateSupport = (isp: ClientISP) => {
    setSupportFormData({
      provider_id: isp.provider_id,
      client_id: isp.client_id,
      isp_id: isp.isp_id,
      isp_service_id: undefined,
      support_title: '',
      support_description: '',
      active_status: true
    });
    setSupportTitleError(false);
    setEditingSupport(null);
    setSupportDialogOpen(true);
  };

  const handleEditSupport = (support: ClientISPSupport) => {
    setSupportFormData({
      provider_id: support.provider_id,
      client_id: support.client_id,
      isp_id: support.isp_id,
      isp_service_id: support.isp_service_id ?? undefined,
      support_title: support.support_title || '',
      support_description: support.support_description || '',
      active_status: support.active_status !== false
    });
    setSupportTitleError(false);
    setEditingSupport(support);
    setSupportDialogOpen(true);
  };

  const handleSaveSupport = async () => {
    if (!supportFormData.support_title.trim()) {
      setSupportTitleError(true);
      setError('Support title is required.');
      return;
    }
    setSupportTitleError(false);
    try {
      setIsLoading(true);
      setError(null);
      
      if (editingSupport) {
        const contentChanged =
          (supportFormData.isp_service_id ?? null) !== (editingSupport.isp_service_id ?? null) ||
          supportFormData.support_title !== (editingSupport.support_title || '') ||
          supportFormData.support_description !== (editingSupport.support_description || '');
        const statusChanged = supportFormData.active_status !== (editingSupport.active_status !== false);

        if (contentChanged) {
          await api.clientISP.supports.update(editingSupport.support_id, {
            isp_service_id: supportFormData.isp_service_id ?? null,
            support_title: supportFormData.support_title,
            support_description: supportFormData.support_description,
            active_status: supportFormData.active_status
          });
          setSuccess('Support revised successfully. Prior version kept for historical visits.');
        } else if (statusChanged) {
          await api.clientISP.supports.update(editingSupport.support_id, {
            active_status: supportFormData.active_status
          });
          setSuccess(supportFormData.active_status ? 'Support activated.' : 'Support deactivated.');
        } else {
          setSupportDialogOpen(false);
          return;
        }
      } else {
        await api.clientISP.supports.create(supportFormData.isp_id, {
          provider_id: supportFormData.provider_id,
          client_id: supportFormData.client_id,
          isp_id: supportFormData.isp_id,
          isp_service_id: supportFormData.isp_service_id,
          support_title: supportFormData.support_title,
          support_description: supportFormData.support_description
        });
        setSuccess('Support created successfully.');
      }
      
      setSupportDialogOpen(false);
      if (selectedClient) {
        await fetchISPsByClient(selectedClient.client_id);
      }
    } catch (err: any) {
      console.error('Error saving support:', err);
      setError(err.response?.data?.detail || 'Failed to save support.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleSupportActive = async (support: ClientISPSupport, active: boolean) => {
    try {
      setIsLoading(true);
      setError(null);
      await api.clientISP.supports.update(support.support_id, { active_status: active });
      setSuccess(active ? 'Support activated.' : 'Support deactivated.');
      if (selectedClient) {
        await fetchISPsByClient(selectedClient.client_id);
      }
    } catch (err: any) {
      console.error('Error updating support status:', err);
      setError(err.response?.data?.detail || 'Failed to update support status.');
    } finally {
      setIsLoading(false);
    }
  };

  const getSelectedServiceIdsForIsp = (ispId: number): number[] => {
    return (ispServices[ispId] || []).map(s => s.service_id);
  };

  const handleAddIspService = async (isp: ClientISP, serviceId: number) => {
    try {
      setIspServicesSaving(prev => ({ ...prev, [isp.isp_id]: true }));
      setError(null);
      await api.clientIspServices.create({
        isp_id: isp.isp_id,
        client_id: isp.client_id,
        provider_id: isp.provider_id,
        service_id: serviceId,
        active_from: easternTodayYmd(),
        active_to: '9999-01-01'
      });
      setSuccess('Service added successfully.');
      if (selectedClient) {
        await fetchISPsByClient(selectedClient.client_id);
      }
    } catch (err: any) {
      console.error('Error adding ISP service:', err);
      setError(err.response?.data?.detail || 'Failed to add service.');
    } finally {
      setIspServicesSaving(prev => ({ ...prev, [isp.isp_id]: false }));
    }
  };

  const handleRemoveIspService = async (isp: ClientISP, ispServiceId: number) => {
    try {
      setIspServicesSaving(prev => ({ ...prev, [isp.isp_id]: true }));
      setError(null);
      await api.clientIspServices.delete(ispServiceId);
      setSuccess('Service removed successfully.');
      if (selectedClient) {
        await fetchISPsByClient(selectedClient.client_id);
      }
    } catch (err: any) {
      console.error('Error removing ISP service:', err);
      setError(err.response?.data?.detail || 'Failed to remove service.');
    } finally {
      setIspServicesSaving(prev => ({ ...prev, [isp.isp_id]: false }));
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, color: '#2D4B73' }}>
        ISP Management
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Provider</InputLabel>
            <Select
              value={selectedProvider ? String(selectedProvider.provider_id) : ''}
              onChange={(e: SelectChangeEvent<string>) => {
                const provider = providers.find(p => String(p.provider_id) === e.target.value);
                handleProviderChange(provider || null);
              }}
              label="Provider"
              sx={fieldOnPageBlueSx}
              MenuProps={selectMenuWhitePaperProps}
            >
              {providers.map((provider) => (
                <MenuItem key={provider.provider_id} value={String(provider.provider_id)}>
                  {provider.first_name} {provider.last_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Client</InputLabel>
            <Select
              value={selectedClient ? String(selectedClient.client_id) : ''}
              onChange={(e: SelectChangeEvent<string>) => {
                const client = clients.find(c => String(c.client_id) === e.target.value);
                handleClientChange(client || null);
              }}
              label="Client"
              disabled={!selectedProvider}
              sx={fieldOnPageBlueSx}
              MenuProps={selectMenuWhitePaperProps}
            >
              {clients.map((client) => (
                <MenuItem key={client.client_id} value={String(client.client_id)}>
                  {client.first_name} {client.last_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {selectedClient && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateISP}
            sx={{ mb: 2 }}
          >
            Create New ISP
          </Button>
        </Box>
      )}

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {selectedClient && isps.length === 0 && !isLoading && (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No ISP records found for this client. Click "Create New ISP" to get started.
          </Typography>
        </Paper>
      )}

      {isps.map((isp) => (
        <Accordion
          key={isp.isp_id}
          expanded={expandedISP === isp.isp_id}
          onChange={(_, isExpanded) => setExpandedISP(isExpanded ? isp.isp_id : false)}
          sx={{ mb: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h6">
                  ISP #{isp.isp_id}
                  {isp.description && ` - ${isp.description}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isp.span_start_date && isp.span_end_date
                    ? `${isp.span_start_date} to ${isp.span_end_date}`
                    : isp.span_start_date
                    ? `Starting ${isp.span_start_date}`
                    : isp.span_end_date
                    ? `Until ${isp.span_end_date}`
                    : 'No date range'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={isp.active_status ? 'Active' : 'Inactive'}
                  color={isp.active_status ? 'success' : 'default'}
                  size="small"
                />
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditISP(isp);
                  }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteISP(isp);
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              {/* Outcomes Section */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6">Outcomes</Typography>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleCreateOutcome(isp)}
                      >
                        <AddIcon />
                      </IconButton>
                    </Box>
                    <List dense>
                      {(ispOutcomes[isp.isp_id] || []).map((outcome) => (
                        <ListItem
                          key={outcome.outcome_id}
                          sx={{ opacity: outcome.active_status === false ? 0.7 : 1, pr: 18 }}
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <span>{outcome.outcome_title || `Outcome ${outcome.outcome_id}`}</span>
                                {outcome.revised_from_id != null && (
                                  <Chip label="Revised" size="small" color="info" variant="outlined" />
                                )}
                              </Box>
                            }
                            secondary={outcome.outcome_description || 'No description'}
                          />
                          <ListItemSecondaryAction>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    checked={outcome.active_status !== false}
                                    disabled={isLoading}
                                    onChange={(e) => handleToggleOutcomeActive(outcome, e.target.checked)}
                                  />
                                }
                                label={outcome.active_status !== false ? 'Active' : 'Inactive'}
                                labelPlacement="start"
                                sx={{
                                  mr: 0.5,
                                  '& .MuiFormControlLabel-label': { fontSize: '0.75rem', minWidth: 52 }
                                }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => handleEditOutcome(outcome)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Box>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                      {(ispOutcomes[isp.isp_id] || []).length === 0 && (
                        <ListItem>
                          <ListItemText primary="No outcomes" secondary="Click + to add" />
                        </ListItem>
                      )}
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {/* Experiences Section */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6">Experiences</Typography>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleCreateExperience(isp)}
                      >
                        <AddIcon />
                      </IconButton>
                    </Box>
                    <List dense>
                      {(ispExperiences[isp.isp_id] || []).map((experience) => {
                        const outcome = (ispOutcomes[isp.isp_id] || []).find(
                          o => o.outcome_id === experience.outcome_id
                        );
                        return (
                          <ListItem
                            key={experience.experience_id}
                            sx={{ opacity: experience.active_status === false ? 0.7 : 1, pr: 18 }}
                          >
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <span>{experience.experience_title || `Experience ${experience.experience_id}`}</span>
                                  {experience.revised_from_id != null && (
                                    <Chip label="Revised" size="small" color="info" variant="outlined" />
                                  )}
                                </Box>
                              }
                              secondary={
                                <>
                                  {experience.experience_description || 'No description'}
                                  {outcome && (
                                    <Typography variant="caption" display="block" color="text.secondary">
                                      Outcome: {outcome.outcome_title}
                                      {outcome.active_status === false ? ' (inactive)' : ''}
                                    </Typography>
                                  )}
                                </>
                              }
                            />
                            <ListItemSecondaryAction>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      size="small"
                                      checked={experience.active_status !== false}
                                      disabled={isLoading}
                                      onChange={(e) => handleToggleExperienceActive(experience, e.target.checked)}
                                    />
                                  }
                                  label={experience.active_status !== false ? 'Active' : 'Inactive'}
                                  labelPlacement="start"
                                  sx={{
                                    mr: 0.5,
                                    '& .MuiFormControlLabel-label': { fontSize: '0.75rem', minWidth: 52 }
                                  }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditExperience(experience)}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Box>
                            </ListItemSecondaryAction>
                          </ListItem>
                        );
                      })}
                      {(ispExperiences[isp.isp_id] || []).length === 0 && (
                        <ListItem>
                          <ListItemText primary="No experiences" secondary="Click + to add" />
                        </ListItem>
                      )}
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {/* Services Section */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Services
                    </Typography>
                    {ispServicesSaving[isp.isp_id] ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2">Saving...</Typography>
                      </Box>
                    ) : (
                      <>
                        <Autocomplete
                          size="small"
                          options={allServices.filter(
                            s => !getSelectedServiceIdsForIsp(isp.isp_id).includes(s.service_id as number)
                          )}
                          getOptionLabel={(option) =>
                            `${option.service_title || option.service_name || 'Service'}${option.code ? ` (${option.code})` : ''}`
                          }
                          value={null}
                          onChange={(_, newValue) => {
                            if (newValue) {
                              handleAddIspService(isp, newValue.service_id as number);
                              setIspServiceSearchInput(prev => ({ ...prev, [isp.isp_id]: '' }));
                            }
                          }}
                          inputValue={ispServiceSearchInput[isp.isp_id] || ''}
                          onInputChange={(_, value) =>
                            setIspServiceSearchInput(prev => ({ ...prev, [isp.isp_id]: value }))
                          }
                          componentsProps={{ paper: { sx: { bgcolor: '#ffffff' } } }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Search services to add..."
                              sx={fieldOnPageBlueSx}
                            />
                          )}
                          sx={{ mb: 2, ...fieldOnPageBlueSx }}
                        />
                        <List dense>
                          {(ispServices[isp.isp_id] || []).map((ispService) => {
                            const service = allServices.find(s => s.service_id === ispService.service_id);
                            const displayName = service
                              ? (service.service_title || service.service_name || `Service ${ispService.service_id}`)
                              : `Service ${ispService.service_id}`;
                            const displayCode = service?.code ? ` (${service.code})` : '';
                            return (
                              <ListItem key={ispService.isp_service_id}>
                                <ListItemText
                                  primary={`${displayName}${displayCode}`}
                                />
                                <ListItemSecondaryAction>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleRemoveIspService(isp, ispService.isp_service_id)}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </ListItemSecondaryAction>
                              </ListItem>
                            );
                          })}
                          {(ispServices[isp.isp_id] || []).length === 0 && (
                            <ListItem>
                              <ListItemText primary="No services" secondary="Add services via search above" />
                            </ListItem>
                          )}
                        </List>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Supports Section */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6">Supports</Typography>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleCreateSupport(isp)}
                      >
                        <AddIcon />
                      </IconButton>
                    </Box>
                    <List dense>
                      {(ispSupports[isp.isp_id] || []).map((support) => {
                        const ispService = support.isp_service_id
                          ? (ispServices[isp.isp_id] || []).find(s => s.isp_service_id === support.isp_service_id)
                          : null;
                        const service = ispService ? allServices.find(s => s.service_id === ispService.service_id) : null;
                        const serviceLabel = service
                          ? `${service.service_title || service.service_name || 'Service'}${service.code ? ` (${service.code})` : ''}`
                          : null;
                        return (
                        <ListItem
                          key={support.support_id}
                          sx={{ opacity: support.active_status === false ? 0.7 : 1, pr: 18 }}
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <span>{support.support_title || `Support ${support.support_id}`}</span>
                                {support.revised_from_id != null && (
                                  <Chip label="Revised" size="small" color="info" variant="outlined" />
                                )}
                              </Box>
                            }
                            secondary={
                              <>
                                {support.support_description || 'No description'}
                                {serviceLabel && (
                                  <Typography variant="caption" display="block" color="text.secondary">
                                    Service: {serviceLabel}
                                  </Typography>
                                )}
                              </>
                            }
                          />
                          <ListItemSecondaryAction>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    checked={support.active_status !== false}
                                    disabled={isLoading}
                                    onChange={(e) => handleToggleSupportActive(support, e.target.checked)}
                                  />
                                }
                                label={support.active_status !== false ? 'Active' : 'Inactive'}
                                labelPlacement="start"
                                sx={{
                                  mr: 0.5,
                                  '& .MuiFormControlLabel-label': { fontSize: '0.75rem', minWidth: 52 }
                                }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => handleEditSupport(support)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Box>
                          </ListItemSecondaryAction>
                        </ListItem>
                        );
                      })}
                      {(ispSupports[isp.isp_id] || []).length === 0 && (
                        <ListItem>
                          <ListItemText primary="No supports" secondary="Click + to add" />
                        </ListItem>
                      )}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}

      {/* ISP Dialog */}
      <Dialog open={ispDialogOpen} onClose={() => setIspDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingISP ? 'Edit ISP' : 'Create New ISP'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={ispFormData.description}
                onChange={(e) => setIspFormData({ ...ispFormData, description: e.target.value })}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <AppDateField
                label="Start Date"
                value={ispFormData.span_start_date || null}
                onChange={v =>
                  setIspFormData({ ...ispFormData, span_start_date: v || '' })
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <AppDateField
                label="End Date"
                value={ispFormData.span_end_date || null}
                onChange={v =>
                  setIspFormData({ ...ispFormData, span_end_date: v || '' })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={ispFormData.active_status}
                    onChange={(e) => setIspFormData({ ...ispFormData, active_status: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIspDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveISP} variant="contained" disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} /> : 'Done'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Outcome Dialog */}
      <Dialog open={outcomeDialogOpen} onClose={() => setOutcomeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingOutcome ? 'Edit Outcome' : 'Create New Outcome'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Outcome Title"
                value={outcomeFormData.outcome_title}
                onChange={(e) => setOutcomeFormData({ ...outcomeFormData, outcome_title: e.target.value })}
                required
                error={outcomeTitleError && !outcomeFormData.outcome_title.trim()}
                helperText={
                  outcomeTitleError && !outcomeFormData.outcome_title.trim() ? 'Outcome title is required' : undefined
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Outcome Description"
                value={outcomeFormData.outcome_description}
                onChange={(e) => setOutcomeFormData({ ...outcomeFormData, outcome_description: e.target.value })}
                multiline
                rows={4}
              />
            </Grid>
            {editingOutcome && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={outcomeFormData.active_status}
                      onChange={(e) => setOutcomeFormData({ ...outcomeFormData, active_status: e.target.checked })}
                    />
                  }
                  label="Active"
                />
                <Typography variant="caption" display="block" color="text.secondary">
                  Content changes create a new revision. Active toggles the current row in place.
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOutcomeDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveOutcome} variant="contained" disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} /> : 'Done'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Experience Dialog */}
      <Dialog open={experienceDialogOpen} onClose={() => setExperienceDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingExperience ? 'Edit Experience' : 'Create New Experience'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Outcome (Optional)</InputLabel>
                <Select
                  value={experienceFormData.outcome_id || ''}
                  onChange={(e) => setExperienceFormData({ ...experienceFormData, outcome_id: e.target.value ? Number(e.target.value) : undefined })}
                  label="Outcome (Optional)"
sx={fieldOnPageBlueSx}
              MenuProps={selectMenuWhitePaperProps}
                >
                  <MenuItem value="">None</MenuItem>
                  {(ispOutcomes[experienceFormData.isp_id] || [])
                    .filter((outcome) =>
                      outcome.active_status !== false ||
                      outcome.outcome_id === experienceFormData.outcome_id
                    )
                    .map((outcome) => (
                    <MenuItem key={outcome.outcome_id} value={outcome.outcome_id}>
                      {outcome.outcome_title || `Outcome ${outcome.outcome_id}`}
                      {outcome.active_status === false ? ' (inactive)' : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Experience Title"
                value={experienceFormData.experience_title}
                onChange={(e) => setExperienceFormData({ ...experienceFormData, experience_title: e.target.value })}
                required
                error={experienceTitleError && !experienceFormData.experience_title.trim()}
                helperText={
                  experienceTitleError && !experienceFormData.experience_title.trim()
                    ? 'Experience title is required'
                    : undefined
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Experience Description"
                value={experienceFormData.experience_description}
                onChange={(e) => setExperienceFormData({ ...experienceFormData, experience_description: e.target.value })}
                multiline
                rows={4}
              />
            </Grid>
            {editingExperience && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={experienceFormData.active_status}
                      onChange={(e) => setExperienceFormData({ ...experienceFormData, active_status: e.target.checked })}
                    />
                  }
                  label="Active"
                />
                <Typography variant="caption" display="block" color="text.secondary">
                  Content changes create a new revision. Active toggles the current row in place.
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExperienceDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveExperience} variant="contained" disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} /> : 'Done'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Support Dialog */}
      <Dialog open={supportDialogOpen} onClose={() => setSupportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingSupport ? 'Edit Support' : 'Create New Support'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel id="support-service-label">Authorized Service</InputLabel>
                <Select
                  labelId="support-service-label"
                  label="Authorized Service"
                  value={supportFormData.isp_service_id ?? ''}
                  onChange={(e) => setSupportFormData({
                    ...supportFormData,
                    isp_service_id: e.target.value === '' ? undefined : (e.target.value as number)
                  })}
sx={fieldOnPageBlueSx}
              MenuProps={selectMenuWhitePaperProps}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {(ispServices[supportFormData.isp_id] || []).map((ispService) => {
                    const service = allServices.find(s => s.service_id === ispService.service_id);
                    const displayName = service
                      ? `${service.service_title || service.service_name || 'Service'}${service.code ? ` (${service.code})` : ''}`
                      : `Service ${ispService.service_id}`;
                    return (
                      <MenuItem key={ispService.isp_service_id} value={ispService.isp_service_id}>
                        {displayName}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Support Title"
                value={supportFormData.support_title}
                onChange={(e) => setSupportFormData({ ...supportFormData, support_title: e.target.value })}
                required
                error={supportTitleError && !supportFormData.support_title.trim()}
                helperText={
                  supportTitleError && !supportFormData.support_title.trim() ? 'Support title is required' : undefined
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Support Description"
                value={supportFormData.support_description}
                onChange={(e) => setSupportFormData({ ...supportFormData, support_description: e.target.value })}
                multiline
                rows={4}
              />
            </Grid>
            {editingSupport && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={supportFormData.active_status}
                      onChange={(e) => setSupportFormData({ ...supportFormData, active_status: e.target.checked })}
                    />
                  }
                  label="Active"
                />
                <Typography variant="caption" display="block" color="text.secondary">
                  Content changes create a new revision. Active toggles the current row in place.
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSupportDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveSupport} variant="contained" disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} /> : 'Done'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => {
        setDeleteDialogOpen(false);
        setDeleteConfirmCount(0);
        setIspToDelete(null);
      }}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="error" />
            Delete ISP Record
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {deleteConfirmCount === 0 && 'Are you sure you want to delete this ISP record? This action cannot be undone.'}
            {deleteConfirmCount === 1 && 'This will permanently delete the ISP record and all associated outcomes, experiences, and supports. Are you absolutely sure?'}
            {deleteConfirmCount >= 2 && 'Final confirmation: This action is irreversible. Type DELETE to confirm.'}
          </Typography>
          {deleteConfirmCount >= 2 && (
            <TextField
              fullWidth
              label="Type DELETE to confirm"
              onChange={(e) => {
                if (e.target.value === 'DELETE') {
                  handleConfirmDelete();
                }
              }}
              sx={{ mt: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDeleteDialogOpen(false);
            setDeleteConfirmCount(0);
            setIspToDelete(null);
          }}>
            Cancel
          </Button>
          {deleteConfirmCount < 2 && (
            <Button onClick={handleConfirmDelete} color="error" variant="contained">
              {deleteConfirmCount === 0 ? 'Yes, Delete' : 'Yes, I\'m Sure'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ISP;

